import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import MediaCard from '@/components/feature/MediaCard';
import NewsRow from '@/components/feature/NewsRow';
import EmptyState from '@/components/feature/EmptyState';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';
import { useFavorites } from '@/hooks/useFavorites';
import { getMediaById, getNews, relatedMedia, sortNewsByDate } from '@/lib/catalog';
import type { Media } from '@/types';

interface MediaDetailViewProps {
  expectedKind: 'movie' | 'tv';
}

export default function MediaDetailView({ expectedKind }: MediaDetailViewProps) {
  const { id = '' } = useParams();
  const item = getMediaById(id);
  const { isFavorite, toggleFavorite } = useFavorites();
  const [toast, setToast] = useState('');

  const kindLabel = expectedKind === 'movie' ? '电影' : '电视剧';

  useDocumentTitle(
    item ? `${item.title} - 影视详情 - 风驰影院` : '影视详情 - 风驰影院',
    item ? item.synopsis.slice(0, 90) : '风驰影院影视作品详情'
  );

  useEffect(() => {
    if (!toast) return;
    const timer = setTimeout(() => setToast(''), 2000);
    return () => clearTimeout(timer);
  }, [toast]);

  if (!item || item.kind !== expectedKind) {
    return (
      <div className="mx-auto w-full max-w-[1240px] px-4 py-16 md:px-6">
        <EmptyState
          icon="ri-file-unknow-line"
          title="未找到该影视作品"
          description="该作品可能已下线或链接有误，去看看其他精彩内容吧。"
        />
        <div className="mt-5 flex justify-center">
          <Link to="/category/movie" className="rounded-md bg-primary-500 px-5 py-2.5 text-sm text-background-50 transition hover:bg-primary-600">
            返回电影列表
          </Link>
        </div>
      </div>
    );
  }

  const favorited = isFavorite(item.id);
  const related = relatedMedia(item as Media, 4);
  const hotNews = sortNewsByDate(getNews()).slice(0, 5);

  const handleShare = async () => {
    const url = window.location.href;
    try {
      await navigator.clipboard.writeText(url);
      setToast('详情页链接已复制');
    } catch {
      setToast('复制失败，请手动复制地址栏链接');
    }
  };

  return (
    <div className="mx-auto w-full max-w-[1240px] px-4 py-6 md:px-6 md:py-10">
      <nav className="flex flex-wrap items-center gap-1.5 text-xs text-foreground-500 md:text-sm" aria-label="面包屑">
        <Link to="/" className="transition hover:text-primary-600">首页</Link>
        <i className="ri-arrow-right-s-line" />
        <Link to={`/category/${expectedKind}`} className="transition hover:text-primary-600">{kindLabel}</Link>
        <i className="ri-arrow-right-s-line" />
        <span className="text-foreground-900">{item.title}</span>
      </nav>

      <div className="mt-5 grid grid-cols-1 gap-8 lg:grid-cols-[1fr_336px]">
        <article>
          <div className="flex flex-col gap-6 sm:flex-row">
            <div className="relative mx-auto aspect-[2/3] w-40 shrink-0 overflow-hidden rounded-lg border border-background-200 bg-background-200 sm:mx-0 sm:w-56">
              <img
                src={item.poster}
                alt={`${item.title} 海报`}
                title={`${item.title} ${kindLabel}详情`}
                className="h-full w-full object-cover object-top"
              />
              <span className="absolute left-2 top-2 flex items-center gap-1 rounded-full bg-accent-500 px-2.5 py-1 text-xs font-bold text-secondary-950">
                <i className="ri-star-fill text-[10px]" />
                {item.rating.toFixed(1)}
              </span>
            </div>

            <div className="min-w-0 flex-1">
              <h1 className="font-heading text-2xl font-extrabold text-foreground-950 md:text-3xl">{item.title}</h1>
              <div className="mt-3 flex flex-wrap gap-2">
                {item.genres.map((genre) => (
                  <span key={genre} className="rounded-full bg-primary-50 px-2.5 py-1 text-xs font-medium text-primary-700">
                    {genre}
                  </span>
                ))}
                <span className="rounded-full bg-secondary-100 px-2.5 py-1 text-xs font-medium text-secondary-900">
                  {item.region}
                </span>
                <span className="rounded-full bg-secondary-100 px-2.5 py-1 text-xs font-medium text-secondary-900">
                  {item.year}
                </span>
              </div>

              <dl className="mt-5 grid grid-cols-1 gap-x-6 gap-y-2.5 text-sm sm:grid-cols-2">
                <div className="flex gap-2">
                  <dt className="shrink-0 text-foreground-500">导演</dt>
                  <dd className="text-foreground-900">{item.director}</dd>
                </div>
                <div className="flex gap-2">
                  <dt className="shrink-0 text-foreground-500">{item.kind === 'movie' ? '时长' : '集数'}</dt>
                  <dd className="text-foreground-900">
                    {item.kind === 'movie' ? item.duration : `${item.episodes}集 · ${item.status}`}
                  </dd>
                </div>
                <div className="flex gap-2 sm:col-span-2">
                  <dt className="shrink-0 text-foreground-500">主演</dt>
                  <dd className="text-foreground-900">{item.cast.join(' / ')}</dd>
                </div>
              </dl>

              <div className="mt-6 flex flex-wrap items-center gap-3">
                <button
                  type="button"
                  onClick={() => {
                    const added = toggleFavorite(item.id);
                    setToast(added ? '已加入我的收藏' : '已取消收藏');
                  }}
                  className={`flex h-10 items-center gap-1.5 rounded-md px-5 text-sm font-medium transition whitespace-nowrap ${
                    favorited
                      ? 'bg-accent-500 text-secondary-950 hover:bg-accent-600'
                      : 'border border-background-300 bg-background-50 text-foreground-800 hover:border-accent-400 hover:text-accent-700'
                  }`}
                >
                  <i className={`${favorited ? 'ri-heart-3-fill' : 'ri-heart-3-line'} text-lg`} />
                  {favorited ? '已收藏' : '收藏'}
                </button>
                <button
                  type="button"
                  onClick={handleShare}
                  className="flex h-10 items-center gap-1.5 rounded-md border border-background-300 bg-background-50 px-5 text-sm font-medium text-foreground-800 transition hover:border-primary-300 hover:text-primary-600 whitespace-nowrap"
                >
                  <i className="ri-share-forward-line text-lg" />
                  分享
                </button>
              </div>
            </div>
          </div>

          <div className="mt-8 flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 bg-background-50 py-10 text-center">
            <span className="flex h-14 w-14 items-center justify-center rounded-full bg-background-100 text-foreground-400">
              <i className="ri-play-circle-line text-3xl" />
            </span>
            <p className="mt-3 font-heading text-sm font-bold text-foreground-700">暂无播放</p>
            <p className="mt-1 text-xs text-foreground-500">本站仅提供影视资源展示与信息查询，暂不提供在线播放功能。</p>
          </div>

          <section className="mt-8 rounded-lg border border-background-200 bg-background-50 p-5 md:p-6">
            <h2 className="flex items-center gap-2 font-heading text-lg font-extrabold text-foreground-950">
              <i className="ri-file-text-line text-primary-500" />
              作品简介
            </h2>
            <p className="mt-4 text-sm leading-7 text-foreground-700 md:text-[15px]">{item.synopsis}</p>
          </section>
        </article>

        <aside className="space-y-8">
          <section>
            <h2 className="mb-4 flex items-center gap-2 font-heading text-base font-extrabold text-foreground-950">
              <i className="ri-links-line text-primary-500" />
              相关推荐
            </h2>
            <div className="grid grid-cols-2 gap-3">
              {related.map((rec) => (
                <MediaCard key={rec.id} item={rec} />
              ))}
            </div>
          </section>

          <section className="rounded-lg border border-background-200 bg-background-50 px-5 py-2">
            <h2 className="flex items-center gap-2 py-3 font-heading text-base font-extrabold text-foreground-950">
              <i className="ri-fire-line text-accent-500" />
              热门资讯
            </h2>
            {hotNews.map((news) => (
              <NewsRow key={news.id} item={news} />
            ))}
          </section>
        </aside>
      </div>

      {toast && (
        <div className="fc-fade fixed bottom-24 left-1/2 z-50 -translate-x-1/2 rounded-full bg-foreground-950/90 px-5 py-2.5 text-sm text-background-50 shadow-lg">
          {toast}
        </div>
      )}
    </div>
  );
}